import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, gbp, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrustBadge } from "@/components/TrustBadge";
import { recordView } from "@/components/home/RecommendedListings";
import { CompareButton } from "@/components/compare/CompareButton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Gauge, Fuel, Calendar, Settings2, Palette, Car, ShieldCheck, MessageSquare, Loader2, Flag, ImageOff } from "lucide-react";

export default function ListingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [l, setL] = useState(null);
  const [active, setActive] = useState(0);
  const [msgOpen, setMsgOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get(`/listings/${id}`).then(({ data }) => { setL(data); recordView(data); }).catch(() => setL(false)); }, [id]);

  if (l === false) return <div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted-foreground">Listing not found.</div>;
  if (!l) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;

  const photos = l.photos || [];
  const isOwner = user && user.user_id === l.seller_id;

  const startMessage = async () => {
    if (!user) { navigate("/login", { state: { from: `/listing/${id}` } }); return; }
    setSending(true);
    try {
      const { data: conv } = await api.post("/conversations", null, { params: { listing_id: id } });
      if (msg.trim()) await api.post(`/conversations/${conv.id}/messages`, { body: msg });
      toast.success("Message sent to seller");
      setMsgOpen(false);
      navigate(`/messages/${conv.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const spec = [
    { icon: Calendar, label: "Year", value: l.year },
    { icon: Gauge, label: "Mileage", value: `${Number(l.mileage).toLocaleString()} mi` },
    { icon: Fuel, label: "Fuel", value: l.fuel || "—" },
    { icon: Settings2, label: "Gearbox", value: l.transmission || "—" },
    { icon: Palette, label: "Colour", value: l.colour || "—" },
    ...(l.power_bhp ? [{ icon: Gauge, label: "Power", value: `${l.power_bhp} bhp` }] : []),
    { icon: Car, label: "Body", value: l.body_type || "—" },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="rounded-2xl overflow-hidden bg-slate-100 aspect-[16/10] border border-border">
            {photos[active] ? <img src={fileUrl(photos[active])} alt="" className="w-full h-full object-cover" data-testid="detail-main-photo" />
              : <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageOff className="w-10 h-10" /></div>}
          </div>
          {photos.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {photos.map((p, i) => (
                <button key={i} onClick={() => setActive(i)} className={`w-24 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${i === active ? "border-accent" : "border-transparent"}`}>
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {l.description && (
            <div className="mt-8"><h2 className="font-heading text-xl font-semibold mb-2">Description</h2>
              <p className="text-muted-foreground whitespace-pre-wrap leading-relaxed">{l.description}</p></div>
          )}
          <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-5 flex gap-3">
            <ShieldCheck className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">Vorra is a direct seller-to-buyer marketplace and is <strong>not a party to this sale</strong>. The seller holds full legal responsibility for the vehicle's condition, description and accuracy. Always inspect a vehicle and verify documents before paying. <Link to="/legal/liability" className="underline font-medium">Platform terms</Link>.</p>
          </div>
        </div>

        <div>
          <div className="sticky top-20 bg-card rounded-2xl border border-border p-6">
            <div className="flex flex-wrap gap-2 mb-3">
              {l.verified ? <TrustBadge type="verified" testId="detail-verified-badge" /> : l.data_checked && <TrustBadge type="data_checked" testId="detail-data-checked-badge" />}
              {l.v5c_status === "pending" && <TrustBadge type="v5c" />}
              {l.mileage_warning && <TrustBadge type="mileage" testId="detail-mileage-badge" />}
            </div>
            <h1 className="font-heading text-2xl font-bold leading-tight">{l.make} {l.model}</h1>
            <p className="text-muted-foreground">{l.variant}</p>
            <div className="font-heading text-4xl font-bold mt-4" data-testid="detail-price">{gbp(l.price)}</div>
            {l.reg && <div className="uk-plate inline-block px-3 py-1 mt-4 text-lg">{l.reg}</div>}
            <div className="grid grid-cols-2 gap-3 mt-6">
              {spec.map((s, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <s.icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div><div className="text-xs text-muted-foreground">{s.label}</div><div className="font-medium">{s.value}</div></div>
                </div>
              ))}
            </div>
            {l.mileage_warning && (
              <div className="mt-4 text-xs bg-orange-50 text-orange-800 border border-orange-200 rounded-lg p-3">
                ⚠ Mileage appears lower than a previous recorded reading. Verify service history before buying.
              </div>
            )}
            {!isOwner ? (
              <Button onClick={() => setMsgOpen(true)} className="w-full mt-6 h-12 bg-accent hover:bg-emerald-600 text-white" data-testid="detail-message-btn">
                <MessageSquare className="w-4 h-4 mr-2" /> Message seller
              </Button>
            ) : (
              <p className="mt-6 text-sm text-center text-muted-foreground">This is your listing.</p>
            )}
            <CompareButton listing={l} variant="detail" />
            <Link to={`/seller/${l.seller_id}`} data-testid="detail-seller-link" className="block text-xs text-muted-foreground mt-3 text-center hover:text-accent">
              Sold by {l.seller_type === "dealer" ? "a dealer" : "a private seller"} · <span className="underline font-medium">{l.seller_type === "dealer" ? "View forecourt" : "View seller's cars"}</span>
            </Link>
          </div>
        </div>
      </div>

      <Dialog open={msgOpen} onOpenChange={setMsgOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Message the seller</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> Keep conversations on Vorra. Never send payment before inspecting the car.</p>
          <Textarea data-testid="message-textarea" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={`Hi, is the ${l.make} ${l.model} still available?`} rows={4} />
          <DialogFooter>
            <Button onClick={startMessage} disabled={sending} className="bg-accent hover:bg-emerald-600 text-white" data-testid="message-send-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send message"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
